import { useEffect, useRef, useState } from 'react'
import { Navigate } from 'react-router'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'
import { AppShell } from '@/components/layout/AppShell'
import { useAuthStore } from '@/store/auth'
import { useUIStore } from '@/store/ui'
import { useSyncStore } from '@/store/sync'
import { fetchGroups } from '@/api/groups'
import { fetchMe } from '@/api/auth'
import { fetchWishlist, fetchPlaying, fetchPlayed } from '@/api/wishlist'
import { fetchRecommendations } from '@/api/recommendations'
import { useGroupWebSocket } from '@/hooks/useGroupWebSocket'
import { pickMoreRecentSeen } from '@/data/changelog'
import type { Friend } from '@/types'

function steamIdToHue(steamId: string): number {
  const n = parseInt(steamId.slice(-4), 10) || 0
  return n % 360
}

// Warm the other group views in the background once the active page has settled,
// so tab switches are instant on the next click. We delay the check by a tick
// because the route's own queries haven't registered observers yet on the same
// render Layout's effect runs — without the delay, useIsFetching reads 0 and
// the prefetches fire in parallel with the active page's fetch.
function useBackgroundPrefetch(groupId: string | null): void {
  const queryClient = useQueryClient()
  const activeFetching = useIsFetching({ type: 'active' })
  const prefetchedRef = useRef<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    setReady(false)
    if (!groupId) return
    const t = window.setTimeout(() => setReady(true), 150)
    return () => window.clearTimeout(t)
  }, [groupId])

  useEffect(() => {
    if (!groupId || !ready) return
    if (activeFetching > 0) return
    if (prefetchedRef.current === groupId) return
    prefetchedRef.current = groupId

    void queryClient.prefetchQuery({
      queryKey: ['wishlist', groupId],
      queryFn: () => fetchWishlist(groupId),
    })
    void queryClient.prefetchQuery({
      queryKey: ['playing', groupId],
      queryFn: () => fetchPlaying(groupId),
    })
    void queryClient.prefetchQuery({
      queryKey: ['played', groupId],
      queryFn: () => fetchPlayed(groupId),
    })
    void queryClient.prefetchQuery({
      queryKey: ['recommendations', groupId],
      queryFn: () => fetchRecommendations(groupId),
    })
  }, [groupId, ready, activeFetching, queryClient])
}

export default function Layout() {
  const { user, setUser } = useAuthStore()
  const { activeGroupId, setActiveGroupId, theme, setTheme } = useUIStore()
  const serverThemeAppliedRef = useRef(false)
  const { isSyncing, setSyncComplete } = useSyncStore()
  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
  })

  // Always fetch profile on mount to pick up server-side changes (e.g. isAdmin).
  // Poll more frequently while bootstrap sync is in progress.
  const { data: freshProfile } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 5 * 60 * 1000,
    refetchInterval: isSyncing ? 5000 : false,
  })

  useEffect(() => {
    if (groups.length === 0) return
    const stillExists = activeGroupId && groups.some((g) => g.id === activeGroupId)
    if (!stillExists) {
      setActiveGroupId(groups[0].id)
    }
  }, [groups, activeGroupId, setActiveGroupId])

  useEffect(() => {
    if (freshProfile && user) {
      // Keep whichever changelogLastSeen is more recent — an in-flight or failed
      // PUT shouldn't let a stale server value clobber a local dismissal.
      setUser({
        ...user,
        ...freshProfile,
        changelogLastSeen: pickMoreRecentSeen(user.changelogLastSeen, freshProfile.changelogLastSeen),
      })
    }
  }, [freshProfile])

  // Reconcile theme with server once per session. Subsequent local changes
  // win — they fire their own PUT and we don't want a stale poll to clobber.
  useEffect(() => {
    if (serverThemeAppliedRef.current) return
    if (!freshProfile) return
    serverThemeAppliedRef.current = true
    if (freshProfile.theme && freshProfile.theme !== theme) {
      setTheme(freshProfile.theme)
    }
  }, [freshProfile, theme, setTheme])

  useEffect(() => {
    if (isSyncing && freshProfile?.libraryLastSyncedAt && freshProfile?.friendsLastSyncedAt) {
      setSyncComplete()
    }
  }, [freshProfile, isSyncing, setSyncComplete])

  useGroupWebSocket(activeGroupId)
  useBackgroundPrefetch(activeGroupId)

  if (!user) {
    return <Navigate to="/login" replace />
  }

  const currentUser: Friend = {
    id: user.steamId,
    name: user.name,
    handle: user.handle,
    status: 'authed',
    hue: steamIdToHue(user.steamId),
    level: '?',
    avatarUrl: user.avatarUrl,
  }

  return <AppShell groups={groups} groupsLoading={groupsLoading} currentUser={currentUser} />
}
